/**
 * Os traços do rastreio e da previsão: cores, espessuras e véus — a ÚNICA
 * fonte do desenho. O mapa (`layer.ts`) e a legenda do painel
 * (`TrackingFloat.tsx`) leem daqui, para não divergirem (a legenda já teve
 * traço de 1,4 px contra 1,3 do mapa, e casco tracejado onde o mapa o tem
 * contínuo). Só números e cores: sem OpenLayers, para servir aos dois.
 */

/**
 * Sistema (primeiro limiar): ocre, um dourado sóbrio. Até 03/11/2026 era o
 * amarelo vivo #ffd84a, que o usuário achou destoante ("marcando muito o
 * contorno das nuvens; quero algo mais profissional"). Comparado sobre o
 * GeoColor e o canal 13 com areia (#e9d18a, sumia na nuvem), marfim (#efe6cc,
 * se confundia com as divisas brancas) e o próprio amarelo a 65%: o ocre
 * se lê como linha de mapa sem gritar. O que mais pesava, porém, era a escada
 * dos pixels: os contornos agora são suavizados (map/suavizar.ts).
 */
export const COR_SISTEMA = '#d6b25e';
/** Núcleo (limiar mais frio): vermelho-rosado, a convecção mais profunda. */
export const COR_NUCLEO = '#ff5d73';
/** Sistema previsto: ciano, para não se confundir com o contorno observado quando os dois quase coincidem. */
export const COR_PREVISAO = '#7fe3ff';
/**
 * Sistema selecionado (o da ficha aberta): violeta. A cor é que diz "este é o
 * meu sistema"; a espessura quase não muda. Por que este violeta:
 * - amarelo, ciano e rosado já têm dono, e as divisas dos estados são brancas;
 * - a escala do infravermelho realçado (canal 13) vai de branco e preto a
 *   vermelho, amarelo, verde, ciano e azul nos topos frios: não tem violeta;
 * - claro e saturado: sobre o casco escuro tem 6,8:1 de contraste, e fica a
 *   mais de 60° de matiz do azul da escala e do rosado do núcleo.
 * Até 29/09/2026 o selecionado era um halo branco de 3,4 px em volta da linha
 * (casco de 5,4 px): o dono do projeto achou grosso.
 */
export const COR_SELECAO = '#b57bff';

export const TRACEJADO = [8, 4];

/** `#rrggbb` → `rgba(r, g, b, alfa)`. */
export function comAlfa(hex: string, alfa: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alfa})`;
}

/** Um traço como ele é desenhado: o casco escuro por baixo e a linha colorida por cima. */
export interface Traco {
  cor: string;
  /** Opacidade da linha. */
  alfa: number;
  /** Espessura da linha (px). */
  largura: number;
  /** Cor (com a opacidade) e espessura do casco (px). */
  casco: string;
  larguraCasco: number;
  /** A linha é tracejada; o casco é sempre contínuo (os vãos ficam escuros, não transparentes). */
  tracejado: boolean;
  /** Preenchimento (`rgba`), ou null para só o toque invisível. */
  veu: string | null;
}

/**
 * Traço fino, mas LEGÍVEL. O que o olho lê como espessura é o conjunto linha
 * + casco: é no casco que se afina. História do observado (linha/casco), toda
 * a pedido do usuário:
 * - 1,6/3,4 px, casco a 65%: pesava sobre a imagem (28/09/2026);
 * - 1/— px a 80%, casco a 32%: sumia sobre as cores vivas do infravermelho;
 * - 1,3/2,8 px, casco a 55%: o meio-termo de 28/09/2026;
 * - 1,1/2,4 px (29/09/2026): "levemente" mais fino — quase imperceptível;
 * - 1/1,8 px, casco a 70% (29/09/2026, depois de publicado): o conjunto caiu
 *   um quarto, e isso se vê de relance. O casco ficou com 0,4 px de cada lado
 *   da linha; para continuar separando o traço da nuvem branca com tão pouco,
 *   subiu de 55% para 70%.
 * - casco a 30% e linha ocre a 90%, contorno suavizado (03/11/2026): o contorno
 *   marcava demais a nuvem. Em escada, a 30% o traço sumia no canal 13; com a
 *   curva suavizada ele se lê, e sem casco nenhum, não (comparado com capturas).
 * A linha não desce de 1 px (`LARGURA_MINIMA`): abaixo disso, em tela de
 * densidade 1, ela serrilha e perde a cor.
 *
 * A espessura NÃO acompanha o zoom. Foi avaliado: a linha já está no piso, de
 * modo que de longe só o casco poderia afinar (0,2 px, que não se percebe), e
 * a legenda e as imagens exportadas deixariam de ter um traço só.
 */
export const LARGURA_MINIMA = 1;
const CASCO = 'rgba(12, 14, 20, 0.3)';

export const TRACO_SISTEMA: Traco = { cor: COR_SISTEMA, alfa: 0.9, largura: 1, casco: CASCO, larguraCasco: 1.8, tracejado: false, veu: null };

/** Véu do núcleo: 5% → 4% → 3%. Com os núcleos de todos os sistemas ligados, somava. */
export const TRACO_NUCLEO: Traco = { ...TRACO_SISTEMA, cor: COR_NUCLEO, veu: comAlfa(COR_NUCLEO, 0.03) };

/**
 * A previsão se distingue do observado pelo tracejado e pela cor. Continua um
 * pouco mais cheia (o tracejado tem dois terços da tinta e costuma cair quase
 * em cima do contorno observado), mas a diferença diminuiu: 1,8/3,6 px →
 * 1,5/3 → 1,2/2,2. O casco é contínuo por baixo (os vãos do tracejado ficam
 * escuros, não transparentes). Véu: 9% → 6% → 5%.
 */
export const TRACO_PREVISTO: Traco = { cor: COR_PREVISAO, alfa: 1, largura: 1.2, casco: CASCO, larguraCasco: 2.2, tracejado: true, veu: comAlfa(COR_PREVISAO, 0.05) };

/** Só o sistema previsto leva o véu: somado ao do núcleo, tingia de rosa a nuvem inteira no GeoColor. */
export const TRACO_NUCLEO_PREVISTO: Traco = { ...TRACO_PREVISTO, cor: COR_NUCLEO, veu: null };

// ---------------------------------------------------------------------------
// Seleção: o sistema da ficha aberta
// ---------------------------------------------------------------------------

/**
 * O selecionado: a LINHA violeta, pouco mais cheia que a normal (1,5 px contra
 * 1), casco fino e mais escuro (2,8 px a 80%, só o bastante para o violeta se
 * ler sobre nuvem branca) e um véu violeta leve (10%; 7% no previsto). Não depende só da cor — a
 * linha é metade mais larga e a área é preenchida —, mas é a cor que carrega
 * o sinal. Os demais contornos NÃO se atenuam: com cor própria no selecionado
 * não é preciso, e o campo fica inteiro.
 */
const SELECIONADO = { cor: COR_SELECAO, alfa: 1, largura: 1.5, casco: 'rgba(8, 10, 16, 0.8)', larguraCasco: 2.8 };

export const TRACO_SISTEMA_SELECIONADO: Traco = { ...TRACO_SISTEMA, ...SELECIONADO, veu: comAlfa(COR_SELECAO, 0.1) };

/**
 * O previsto do sistema selecionado: também violeta, e tracejado — é
 * estimativa, não observação. Véu mais leve: ele se soma ao do observado onde
 * os dois se sobrepõem.
 */
export const TRACO_PREVISTO_SELECIONADO: Traco = { ...TRACO_PREVISTO, ...SELECIONADO, veu: comAlfa(COR_SELECAO, 0.07) };

/**
 * O núcleo SELECIONADO (a ficha aberta é a dele) fica violeta. Já os núcleos
 * do sistema selecionado continuam rosados, dentro do contorno violeta: a
 * distinção entre núcleo e sistema não se perde.
 */
export const TRACO_NUCLEO_SELECIONADO: Traco = { ...TRACO_NUCLEO, ...SELECIONADO, veu: comAlfa(COR_SELECAO, 0.12) };
export const TRACO_NUCLEO_PREVISTO_SELECIONADO: Traco = { ...TRACO_NUCLEO_PREVISTO, ...SELECIONADO, veu: comAlfa(COR_SELECAO, 0.07) };

// ---------------------------------------------------------------------------
// Cone de incerteza do selecionado (trajetoria.ts, cone.ts)
// ---------------------------------------------------------------------------

/**
 * Opacidade do ciano de cada faixa do cone: 0–1 h, 1–2 h, 2–3 h. Cai com a
 * antecedência: a área mais longe é a maior, e com o mesmo alfa ganharia
 * destaque só por isso. As faixas são anéis (não se somam). Até 09/10/2026 o
 * selecionado tinha as linhas do caminho passado e da projeção, em violeta.
 */
export const ALFA_DAS_FAIXAS_DO_CONE: readonly number[] = [0.22, 0.14, 0.08];

// ---------------------------------------------------------------------------
// Rastreio da chuva (src/rastreioChuva)
// ---------------------------------------------------------------------------

/**
 * Área de chuva (≥ 0,1 mm/h, GSMaP): verde-menta. Amarelo (nuvem), ciano
 * (previsão), rosado (núcleo), violeta (seleção) e branco (divisas) já têm
 * dono (o ocre da nuvem também); a escala da chuva vai do azul-claro ao magenta, e o verde se lê sobre
 * ela, sobre o GeoColor e sobre o cinza do infravermelho. Mesma espessura e
 * casco do contorno da nuvem: com os dois rastreios ligados, só a cor muda.
 */
export const COR_CHUVA = '#62f58f';
export const TRACO_CHUVA: Traco = { ...TRACO_SISTEMA, cor: COR_CHUVA };
export const TRACO_CHUVA_SELECIONADO: Traco = { ...TRACO_SISTEMA_SELECIONADO };
